import { useState } from "react";

/** Theme choice: follow the system, or force light or dark. Only this preference is stored. */
export type Theme = "system" | "light" | "dark";

const KEY = "prisma.tema";
const NEXT: Record<Theme, Theme> = { system: "light", light: "dark", dark: "system" };

function read(): Theme {
  try {
    const v = localStorage.getItem(KEY);
    return v === "light" || v === "dark" ? v : "system";
  } catch {
    return "system";
  }
}

function apply(theme: Theme) {
  // index.html keeps following the system while nothing is stored.
  const dark =
    theme === "system" ? matchMedia("(prefers-color-scheme: dark)").matches : theme === "dark";
  document.documentElement.setAttribute("data-theme", dark ? "dark" : "light");
  try {
    if (theme === "system") localStorage.removeItem(KEY);
    else localStorage.setItem(KEY, theme);
  } catch {
    // Private mode or blocked storage: the choice lasts only for this page.
  }
}

/** Current theme and a function that moves to the next one (system → light → dark). */
export function useTheme(): [Theme, () => void] {
  const [theme, setTheme] = useState(read);
  return [
    theme,
    () => {
      const next = NEXT[theme];
      apply(next);
      setTheme(next);
    },
  ];
}
